import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, Gauge, Fuel, Settings2, Calendar, 
  Palette, Zap, CreditCard, Phone, CheckCircle,
  ChevronLeft, ChevronRight, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CarDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ email: "", name: "" });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const response = await axios.get(`${API}/cars/${id}`);
        setCar(response.data);
      } catch (error) {
        console.error("Error fetching car:", error);
        toast.error("Erreur lors du chargement du véhicule");
      } finally {
        setLoading(false);
      }
    };
    fetchCar();
  }, [id]);

  // Check for payment status from URL
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      toast.info("Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.");
    }
  }, [searchParams]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatKm = (km) => {
    return new Intl.NumberFormat('fr-FR').format(km);
  };

  const handleCheckout = async () => {
    if (!customerInfo.email || !customerInfo.name) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        car_id: id,
        origin_url: window.location.origin,
        customer_email: customerInfo.email,
        customer_name: customerInfo.name
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container py-32">
        <div className="animate-pulse space-y-8">
          <div className="h-96 bg-gray-200 rounded-xl" />
          <div className="h-8 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-200 rounded w-1/3" />
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="page-container py-32 text-center">
        <h2 className="text-2xl text-gray-900 mb-4">Véhicule non trouvé</h2>
        <Link to="/voitures">
          <Button>Retour aux véhicules</Button>
        </Link>
      </div>
    );
  }

  const specs = [
    { icon: Calendar, label: "Année", value: car.annee },
    { icon: Gauge, label: "Kilométrage", value: `${formatKm(car.kilometrage)} km` },
    { icon: Fuel, label: "Carburant", value: car.carburant },
    { icon: Settings2, label: "Transmission", value: car.transmission },
    { icon: Zap, label: "Puissance", value: car.puissance },
    { icon: Palette, label: "Couleur", value: car.couleur },
  ];

  return (
    <div data-testid="car-detail-page" className="bg-white">
      <div className="page-container py-12">
        {/* Back button */}
        <Link to="/voitures" className="inline-flex items-center gap-2 text-gray-500 hover:text-yellow-600 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          Retour aux véhicules
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div 
              className="relative cursor-pointer group"
              onClick={() => setShowLightbox(true)}
            >
              <img
                src={getImageUrl(car.images?.[currentImageIndex]) || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1200"}
                alt={`${car.marque} ${car.modele}`}
                className="w-full aspect-[4/3] object-cover rounded-xl shadow-lg"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-xl flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-4 py-2 rounded-full text-sm">
                  Cliquez pour agrandir
                </span>
              </div>
              <div className="absolute top-4 right-4">
                {car.disponible ? (
                  <span className="badge-disponible text-sm px-4 py-2">Disponible</span>
                ) : (
                  <span className="badge-vendu text-sm px-4 py-2">Vendu</span>
                )}
              </div>
              {/* Navigation arrows */}
              {car.images?.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(prev => prev === 0 ? car.images.length - 1 : prev - 1);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-800" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(prev => prev === car.images.length - 1 ? 0 : prev + 1);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-800" />
                  </button>
                </>
              )}
              {/* Image counter */}
              {car.images?.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {car.images.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {car.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {car.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                      index === currentImageIndex 
                        ? 'border-yellow-500' 
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`Vue ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {car.marque} {car.modele}
            </h1>
            <p className="text-gray-500 mb-6">{car.annee}</p>

            <div className="text-4xl font-bold text-yellow-500 mb-8">
              {formatPrice(car.prix)}
            </div>

            {/* Specs Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {specs.map((spec, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 border border-gray-200 rounded-xl p-4"
                >
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <spec.icon className="w-4 h-4" />
                    <span className="text-xs">{spec.label}</span>
                  </div>
                  <div className="text-gray-900 font-medium">{spec.value}</div>
                </div>
              ))}
            </div>

            {/* Description */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{car.description}</p>
            </div>

            {/* CTAs */}
            <div className="space-y-4">
              {car.disponible ? (
                <>
                  <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                    <DialogTrigger asChild>
                      <Button 
                        className="w-full bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase h-14 text-lg"
                        data-testid="buy-button"
                      >
                        <CreditCard className="w-5 h-5 mr-2" />
                        Acheter ce véhicule
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-gray-200">
                      <DialogHeader>
                        <DialogTitle className="text-gray-900">Finaliser l'achat</DialogTitle>
                        <DialogDescription className="text-gray-500">
                          {car.marque} {car.modele} - {formatPrice(car.prix)}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label htmlFor="name" className="text-gray-600">Nom complet</Label>
                          <Input
                            id="name"
                            value={customerInfo.name}
                            onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                            placeholder="Jean Dupont"
                            className="bg-white border-gray-300 mt-2"
                            data-testid="customer-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-gray-600">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            value={customerInfo.email}
                            onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                            placeholder="jean@example.com"
                            className="bg-white border-gray-300 mt-2"
                            data-testid="customer-email-input"
                          />
                        </div>
                        <Button 
                          onClick={handleCheckout}
                          disabled={checkoutLoading}
                          className="w-full bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase"
                          data-testid="proceed-payment-button"
                        >
                          {checkoutLoading ? "Chargement..." : "Procéder au paiement"}
                        </Button>
                        <p className="text-xs text-gray-500 text-center">
                          Paiement sécurisé par Stripe
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-900 font-semibold mb-2">Ce véhicule a été vendu</p>
                  <p className="text-gray-500 text-sm mb-4">
                    Consultez nos autres véhicules disponibles
                  </p>
                  <Link to="/voitures">
                    <Button className="bg-yellow-500 text-white hover:bg-yellow-600">
                      Voir les véhicules disponibles
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && car.images?.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setShowLightbox(false)}
        >
          {/* Close button */}
          <button 
            className="absolute top-6 right-6 text-white hover:text-yellow-500 transition-colors z-10"
            onClick={() => setShowLightbox(false)}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Main image */}
          <div className="relative max-w-6xl max-h-[90vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(car.images[currentImageIndex])}
              alt={`${car.marque} ${car.modele} - Vue ${currentImageIndex + 1}`}
              className="w-full h-full object-contain"
            />

            {/* Navigation */}
            {car.images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImageIndex(prev => prev === 0 ? car.images.length - 1 : prev - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
                >
                  <ChevronLeft className="w-8 h-8 text-white" />
                </button>
                <button
                  onClick={() => setCurrentImageIndex(prev => prev === car.images.length - 1 ? 0 : prev + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
                >
                  <ChevronRight className="w-8 h-8 text-white" />
                </button>
              </>
            )}

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full">
              {currentImageIndex + 1} / {car.images.length}
            </div>
          </div>

          {/* Thumbnails at bottom */}
          {car.images.length > 1 && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2">
              {car.images.map((img, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-colors ${
                    index === currentImageIndex 
                      ? 'border-yellow-500' 
                      : 'border-transparent hover:border-white/50'
                  }`}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Vue ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CarDetail;
